"use client";

import type { CSSProperties, ReactNode } from "react";

import type { Theme } from "../theme";
import { useEditorialTheme } from "../theme-context";

/**
 * The product-component bridge.
 *
 * The marketing site and the product application are two separate Next apps
 * with no shared UI package, but the product's primitives are entirely
 * token-driven — every colour in them resolves through `hsl(var(--token))`.
 * That is the seam. `ProductBridge` re-declares the product's semantic tokens
 * on a single wrapper, so a component imported unchanged out of `apps/app`
 * paints itself in the marketing palette without one line of its source
 * changing.
 *
 * Two deliberate non-goals:
 *
 * 1. **Fonts are not bridged.** The product's face is Inter and it stays
 *    Inter here; the board around it is Figtree. `apps/web` already loads
 *    Inter (`--font-inter`, see `app/layout.tsx`), so this costs nothing and
 *    the component reads exactly as it does in the application.
 * 2. **Only ground, ink and primary are overridden.** Everything else keeps
 *    the product's own value, read from `apps/app/src/app/globals.css`. The
 *    point is fidelity: the palette should re-ink the component, not redesign
 *    it. On the board's default `product` palette the two sets coincide
 *    exactly, which is why that palette exists.
 */

/** Where a bridged token gets its value. */
export type BridgeTokenOrigin = "app" | "palette";

export type BridgeToken = {
  /** The product's semantic token, consumed downstream as `hsl(var(--token))`. */
  token: string;
  origin: BridgeTokenOrigin;
  /**
   * `app` — the literal value from `apps/app/src/app/globals.css`.
   * `palette` — the editorial variable whose colour wins instead.
   */
  basis: string;
  /**
   * How downstream code consumes the token. Almost everything is a bare HSL
   * triplet read through `hsl(var(--token))`; the chart hues are complete
   * colours passed raw to recharts (`fill: var(--chart-1)`), so their value
   * must already be paintable.
   */
  form?: "triplet" | "color";
  note: string;
};

/**
 * The mapping, as data. Rendered verbatim by the BRG-003 specimen so the
 * mechanism is reviewable rather than asserted — the ledger on the board and
 * the CSS on the wrapper are generated from this one array.
 *
 * `app` rows are transcribed from `apps/app/src/app/globals.css` `:root`;
 * `--primary` and `--ring` are transcribed from its `.theme-editorial` block,
 * which is already the same claret as the marketing `--theme-dark`.
 */
export const BRIDGE_TOKENS: readonly BridgeToken[] = [
  {
    token: "--background",
    origin: "palette",
    basis: "--theme-ground",
    note: "The board's ground, so the component sits on the page it is printed on.",
  },
  {
    token: "--foreground",
    origin: "palette",
    basis: "--theme-ink",
    note: "The board's ink; the product's body colour follows the surrounding text.",
  },
  { token: "--card", origin: "app", basis: "40 16% 96%", note: "Product panel fill." },
  { token: "--card-foreground", origin: "app", basis: "225 15% 13%", note: "Text on a panel." },
  { token: "--popover", origin: "app", basis: "0 0% 100%", note: "Menus and popovers." },
  {
    token: "--popover-foreground",
    origin: "app",
    basis: "225 15% 13%",
    note: "Text inside a popover.",
  },
  {
    token: "--primary",
    origin: "palette",
    basis: "--theme-dark",
    note: "The claret. Already identical to the product's own .theme-editorial primary.",
  },
  {
    token: "--primary-foreground",
    origin: "app",
    basis: "0 0% 100%",
    note: "White reads on the claret in both systems.",
  },
  { token: "--secondary", origin: "app", basis: "40 20% 97%", note: "Quiet fill." },
  {
    token: "--secondary-foreground",
    origin: "app",
    basis: "222.2 47.4% 11.2%",
    note: "Text on a quiet fill.",
  },
  { token: "--muted", origin: "app", basis: "40 16% 93%", note: "Hover and well fill." },
  {
    token: "--muted-foreground",
    origin: "app",
    basis: "220 10% 34%",
    note: "Secondary prose — the product's own contrast step, kept exactly.",
  },
  { token: "--accent", origin: "app", basis: "38 28% 96.5%", note: "Selected-row fill." },
  {
    token: "--accent-foreground",
    origin: "app",
    basis: "222.2 47.4% 11.2%",
    note: "Text on the accent fill.",
  },
  {
    token: "--border",
    origin: "app",
    basis: "220 10% 88%",
    note: "Hairline. #DDDFE3 — a touch cooler than the editorial rule, and left alone.",
  },
  { token: "--input", origin: "app", basis: "38 36% 86%", note: "Field edge." },
  {
    token: "--ring",
    origin: "palette",
    basis: "--theme-dark",
    note: "Focus ring follows the claret, as it does under .theme-editorial.",
  },
  { token: "--destructive", origin: "app", basis: "0 84% 55%", note: "Failure state." },
  {
    token: "--destructive-foreground",
    origin: "app",
    basis: "0 0% 100%",
    note: "Text on a failure fill.",
  },
  {
    token: "--content-code-bg",
    origin: "app",
    basis: "38 36% 86%",
    note: "Inline-code chip fill, as the product's CodeBlock paints it.",
  },
  {
    token: "--content-code-border",
    origin: "app",
    basis: "0 0% 82%",
    note: "Inline-code chip hairline.",
  },
  {
    token: "--content-code-foreground",
    origin: "app",
    basis: "225 12% 16%",
    note: "Ink on an inline-code chip.",
  },
  {
    token: "--surface",
    origin: "app",
    basis: "40 24% 99%",
    note: "Elevated panel fill — table rows, dashboard widgets and artifact cards composite onto it.",
  },
  {
    token: "--surface-raised",
    origin: "app",
    basis: "40 20% 99.5%",
    note: "The step above --surface; kanban cards sit on it.",
  },
  {
    token: "--kanban-card-bg",
    origin: "app",
    basis: "hsl(var(--surface-raised))",
    form: "color",
    note: "Board card fill, referencing the raised surface.",
  },
  {
    token: "--kanban-card-hover-bg",
    origin: "app",
    basis: "hsl(var(--muted))",
    form: "color",
    note: "Board card fill on hover.",
  },
  {
    token: "--badge-green-tone",
    origin: "app",
    basis: "145 65% 36%",
    note: "Created-badge tint, at the product's own opacities.",
  },
  {
    token: "--badge-green-foreground",
    origin: "app",
    basis: "145 70% 27%",
    note: "Created-badge text.",
  },
  {
    token: "--badge-sky-tone",
    origin: "app",
    basis: "199 82% 45%",
    note: "Updated-badge tint.",
  },
  {
    token: "--badge-sky-foreground",
    origin: "app",
    basis: "201 78% 34%",
    note: "Updated-badge text.",
  },
  {
    token: "--badge-amber-tone",
    origin: "app",
    basis: "38 88% 42%",
    note: "Data Set mention tint.",
  },
  {
    token: "--badge-amber-foreground",
    origin: "app",
    basis: "32 85% 29%",
    note: "Data Set mention text.",
  },
  {
    token: "--badge-violet-tone",
    origin: "app",
    basis: "263 72% 57%",
    note: "Channel mention tint.",
  },
  {
    token: "--badge-violet-foreground",
    origin: "app",
    basis: "263 68% 42%",
    note: "Channel mention text.",
  },
  {
    token: "--badge-teal-tone",
    origin: "app",
    basis: "173 72% 34%",
    note: "View mention tint.",
  },
  {
    token: "--badge-teal-foreground",
    origin: "app",
    basis: "173 72% 27%",
    note: "View mention text.",
  },
  {
    token: "--solid",
    origin: "app",
    basis: "17 88% 40%",
    note: "Primary action fill — the product's ember CTA.",
  },
  {
    token: "--solid-hover",
    origin: "app",
    basis: "17 88% 34%",
    note: "Primary action fill, hovered.",
  },
  {
    token: "--solid-foreground",
    origin: "app",
    basis: "0 0% 100%",
    note: "Text on the primary action.",
  },
  {
    token: "--chart-1",
    origin: "app",
    basis: "oklch(0.646 0.222 41.116)",
    form: "color",
    note: "Dashboard series hue 1 — the single-series accent.",
  },
  {
    token: "--chart-2",
    origin: "app",
    basis: "oklch(0.6 0.118 184.704)",
    form: "color",
    note: "Dashboard series hue 2.",
  },
  {
    token: "--chart-3",
    origin: "app",
    basis: "oklch(0.57 0.172 258.338)",
    form: "color",
    note: "Dashboard series hue 3.",
  },
  {
    token: "--chart-4",
    origin: "app",
    basis: "oklch(0.719 0.157 74.843)",
    form: "color",
    note: "Dashboard series hue 4.",
  },
  {
    token: "--chart-5",
    origin: "app",
    basis: "oklch(0.637 0.237 25.331)",
    form: "color",
    note: "Dashboard series hue 5.",
  },
] as const;

/**
 * The application's dark-blue appearance, transcribed from its `.dark` block
 * with the `.theme-dark-blue` overrides applied (apps/app/src/app/globals.css)
 * — `--card` resolved through `--sidebar-panel` as the app does. When a
 * bridge runs dark, the editorial palette deliberately does not re-ink
 * anything: the point of a dark exhibit is the application's own appearance
 * standing beside the editorial claret, so every token is the app's.
 */
const APP_DARK_BLUE_VALUES: Record<string, string> = {
  "--background": "225 8% 8%",
  "--foreground": "0 0% 94%",
  "--card": "225 8% 10%",
  "--card-foreground": "0 0% 96%",
  "--popover": "225 7% 13%",
  "--popover-foreground": "0 0% 96%",
  "--primary": "216 88% 76%",
  "--primary-foreground": "225 8% 8%",
  "--secondary": "225 8% 11%",
  "--secondary-foreground": "0 0% 94%",
  "--muted": "225 7% 12%",
  "--muted-foreground": "0 0% 76%",
  "--accent": "225 7% 13%",
  "--accent-foreground": "0 0% 94%",
  "--border": "225 6% 18%",
  "--input": "0 0% 11%",
  "--ring": "218 72% 60%",
  "--destructive": "4 82% 72%",
  "--destructive-foreground": "225 8% 8%",
  "--content-code-bg": "0 0% 11%",
  "--content-code-border": "0 0% 20%",
  "--content-code-foreground": "0 0% 94%",
  "--surface": "225 7% 12%",
  "--surface-raised": "225 7% 13%",
  "--kanban-card-bg": "hsl(var(--surface-raised))",
  "--kanban-card-hover-bg":
    "color-mix(in oklab, hsl(var(--foreground)) 4%, hsl(var(--surface-raised)))",
  "--badge-amber-tone": "40 55% 55%",
  "--badge-amber-foreground": "40 52% 70%",
  "--badge-violet-tone": "263 50% 62%",
  "--badge-violet-foreground": "263 48% 75%",
  "--badge-teal-tone": "173 45% 50%",
  "--badge-teal-foreground": "173 42% 68%",
  "--badge-green-tone": "145 45% 50%",
  "--badge-green-foreground": "145 42% 68%",
  "--badge-sky-tone": "199 55% 56%",
  "--badge-sky-foreground": "199 52% 71%",
  "--solid": "218 72% 50%",
  "--solid-hover": "217 78% 53%",
  "--solid-foreground": "0 0% 100%",
  "--chart-1": "hsl(217 78% 67%)",
  "--chart-2": "oklch(0.696 0.17 162.48)",
  "--chart-3": "oklch(0.735 0.168 264.668)",
  "--chart-4": "oklch(0.778 0.16 84.107)",
  "--chart-5": "oklch(0.704 0.191 22.216)",
};

/** Round to one decimal so the ledger prints readable triplets. */
function round(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Convert a palette hex into the space-separated HSL triplet the product's
 * tokens are declared in. The product consumes every token as
 * `hsl(var(--token))`, so a hex would produce an invalid colour; the triplet
 * is the only form that composes with the opacity modifiers the product's
 * primitives use (`ring-ring/50`, `bg-destructive/10`).
 */
export function hexToHslTriplet(hex: string): string {
  const clean = hex.trim().replace(/^#/, "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => `${c}${c}`)
          .join("")
      : clean.slice(0, 6);
  const int = Number.parseInt(full, 16);
  if (full.length !== 6 || Number.isNaN(int)) return "0 0% 0%";

  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const delta = max - min;

  let h = 0;
  let s = 0;
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`;
}

/** The live palette colour a `palette`-origin token resolves to. */
function paletteHex(theme: Theme, basis: string): string {
  if (basis === "--theme-ground") return theme.ground;
  if (basis === "--theme-ink") return theme.ink;
  if (basis === "--theme-accent") return theme.accent;
  return theme.dark;
}

export type ResolvedBridgeToken = BridgeToken & {
  /** The value actually written onto the wrapper. */
  value: string;
  /** The value as a paintable CSS colour, for the ledger's swatch. */
  swatch: string;
  /** The palette hex behind it, for `palette`-origin rows. */
  hex?: string;
};

/**
 * Resolve the mapping against a palette. Exported so the BRG-003 specimen can
 * print the same numbers the wrapper is carrying, rather than a transcription
 * of them.
 */
export function resolveBridgeTokens(theme: Theme): ResolvedBridgeToken[] {
  return BRIDGE_TOKENS.map((entry) => {
    if (entry.origin === "app") {
      return {
        ...entry,
        value: entry.basis,
        swatch: entry.form === "color" ? entry.basis : `hsl(${entry.basis})`,
      };
    }
    const hex = paletteHex(theme, entry.basis);
    const value = hexToHslTriplet(hex);
    return { ...entry, value, swatch: `hsl(${value})`, hex };
  });
}

/**
 * Corner treatment.
 *
 * `--radius` is a token like any other, and the editorial system's value for
 * it is zero — the marketing site already sets `data-radius="square"` on
 * `<html>`, so square is what a bridged component gets for free and `product`
 * is the setting that costs an override. Squaring was tried against the
 * product's own 0.5rem and kept: nothing in these components depends on the
 * radius (a single small button changes shape), and the rounded version reads
 * like a screenshot pasted onto a drawing sheet rather than something printed
 * on it. `product` remains available for a specimen that has to argue the
 * opposite.
 */
export type BridgeCorners = "square" | "product";

export type BridgeAppearance = "light" | "dark" | "dark-blue";

export function ProductBridge({
  children,
  corners = "square",
  appearance = "light",
  className,
  style,
}: {
  children: ReactNode;
  /** `square` keeps the editorial system's zero radius (default); `product`
   *  restores the application's 0.5rem. */
  corners?: BridgeCorners;
  /** `light` (default) is the bridged mapping — app values re-inked by the
   *  palette. `dark` (and its older alias `dark-blue`) is the application's
   *  own dark appearance, verbatim: a dark product asset is always the
   *  product's night, whatever palette frames it. */
  appearance?: BridgeAppearance;
  className?: string;
  style?: CSSProperties;
}) {
  const { theme } = useEditorialTheme();
  const vars: Record<string, string> = {};
  for (const entry of resolveBridgeTokens(theme)) {
    vars[entry.token] =
      appearance === "light"
        ? entry.value
        : (APP_DARK_BLUE_VALUES[entry.token] ?? entry.value);
  }
  // "dark" and "dark-blue" now behave identically: the application's own
  // dark appearance, verbatim. The section-matching experiment retired with
  // the whole-dark sheets — palette darks belong to the editorial surfaces,
  // and a dark product asset is always the product's own night.

  return (
    <div
      data-product-bridge=""
      data-corners={corners}
      className={className}
      style={
        {
          ...vars,
          "--radius": corners === "product" ? "0.5rem" : "0rem",
          "--radius-pill": corners === "product" ? "calc(0.5rem - 2px)" : "0rem",
          // The product's face, deliberately unbridged — and pinned to the
          // application's own body: its exact sans stack, weight 400 (the
          // marketing body is 450, which must not bleed into a product
          // surface), its 1.6 leading and its antialiasing. All transcribed
          // from the `body` rule in apps/app/src/app/globals.css.
          fontFamily:
            'var(--font-inter), "ABCDiatype", ui-sans-serif, system-ui, sans-serif',
          fontWeight: 400,
          lineHeight: 1.6,
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
          textRendering: "optimizeLegibility",
          // The non-colour token the imported typeset reads
          // (`--typeset-font-mono` resolves to it), written here rather than
          // through BRIDGE_TOKENS. Transcribed from the product's own `body`
          // declaration; the editorial mono (`--font-departure-mono`) is
          // deliberately not used, because inline code inside a governed
          // answer should read as the application's code, not as the board's.
          // `apps/web` mirrors the application's JetBrains Mono under
          // `--font-jetbrains-mono` (see app/layout.tsx), so the chain
          // resolves to the product's actual mono, not a system stand-in.
          "--font-mono":
            'var(--font-jetbrains-mono, var(--font-source-code-pro, ui-monospace, "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace))',
          // The wrapper paints the ground it declares, so anything that
          // inherits colour rather than reading a token still lands on-palette.
          backgroundColor: "hsl(var(--background))",
          color: "hsl(var(--foreground))",
          ...style,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
}
